import { TimestampField } from '@prismicio/client';
import { format, isAfter, isBefore, parse } from 'date-fns';
import startCase from 'lodash/startCase';

import { PLACEHOLDER, TEMPLATE_BASE_PATH } from '@/constants';
import { getDistance, initializeApollo, IStoreMetadata } from '@/data';
import {
  Asset,
  CannabisType,
  Component,
  DayPeriod,
  Discount,
  Geoloc,
  GProduct,
  PriceType,
  ProductList,
  PromoCarouselDocumentDataCarouselItemsItemWithID,
  ScheduleWorkingHours,
  SubCat,
  TierMethod,
  TierPricingDetails,
  TreezProduct,
  TreezSpecials,
  TreezStore,
  Variant,
  WeekDay,
} from '@/types';

import { stringToSlug } from './utils';
import { toZonedDate } from './toZonedDate';

import data from '@/public/ssr-cache.json';

const client = initializeApollo();

const mapStoreToLocation = ({
  name,
  _geoloc: { lat, lng },
  google_map_url,
  photo,
  url_slug,
}): {
  label: string;
  lat: number;
  lng: number;
  title: string;
  google_map_url?: string;
  photo?: string;
  url_slug: string;
} => ({
  label: name,
  lat,
  lng,
  title: name,
  google_map_url,
  photo,
  url_slug,
});

const compareDistances = (firstStore, secondStore) => {
  return firstStore.distance - secondStore.distance;
};

const mapStoreToDistance =
  (currentLocation: Geoloc) => async (store: TreezStore) => {
    const metricSystem = process.env.NEXT_PUBLIC_DISTANCE_MEASUREMENT_UNIT;
    const { lat, lng } = mapStoreToLocation(store);

    const { data, error } = await getDistance(client, {
      variables: {
        from: { latitude: lat, longitude: lng },
        to: {
          latitude: currentLocation.lat,
          longitude: currentLocation.lng,
        },
      },
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('getDistance', error);
      return { ...store };
    }

    const currentDimension = data.find(
      d => d.dimension.toUpperCase() === metricSystem?.toUpperCase(),
    );

    if (!currentDimension) {
      return { ...store };
    }

    return { ...store, distance: currentDimension.value };
  };

// eslint-disable-next-line no-unused-vars
const checkIfStoreIsOpen = (openingTime: string, closingTime: string) => {
  return true;
};

const getHoursAndMinutes = (time: string | null) => {
  if (!time) {
    return {
      hours: 10,
      minutes: 10,
    };
  }

  const [stringHours, stringMinutes] = time.split(':');
  return {
    hours: parseInt(stringHours, 10),
    minutes: parseInt(stringMinutes, 10),
  };
};

const transformMilitaryTime = (militaryFormatTime: string): string => {
  if (!militaryFormatTime) {
    return '';
  }

  const dateObject = parse(militaryFormatTime, 'HH:mm', new Date());

  return format(dateObject, 'h:mm aa');
};

const MapProducts = (products: TreezProduct[]): GProduct[] => {
  return products?.map(mapProduct);
};

// eslint-disable-next-line no-unused-vars
const sanitizeDescription = (description: string) => {
  if (!description) {
    return '';
  }

  return description.replace(/[\r\n]+/g, ' ');
};

const formatProductTitle = (productTitle: string) => {
  try {
    const signalsToIgnore = [
      'CBD-',
      'CBD',
      'Grams',
      'OG',
      'THC-',
      'THC',
      'CBN-',
      'CBN',
      'CBG',
      'CBG-',
      'CBC',
      'CBC-',
      'THCA',
      'THCA-',
      'CBDA',
      'CBDA-',
      'SFV',
      'SFV-',
    ];

    const containsAny = (str: string, arr: string[]) => {
      return arr.some(subStr => str.includes(subStr));
    };

    return productTitle
      .split(' ')
      .map(word => {
        if (!isNaN(+word.charAt(0)) || containsAny(word, signalsToIgnore)) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase();
      })
      .join(' ');
  } catch (error) {
    return productTitle;
  }
};

const findCategoryForSubtype = (subCat: SubCat, subtype: string) => {
  for (const [category, subtypes] of Object.entries(subCat)) {
    if (subtypes.includes(subtype)) {
      return stringToSlug(category);
    }
  }
  return '';
};

const getDescription = (product: TreezProduct): string => {
  if (product?.description) {
    return product.description;
  }

  if (product?.longDescription) {
    return product.longDescription;
  }

  return product?.shortDescription;
};

export const PRODUCT_URL_PATH = '/product/';

const priceType = (product: TreezProduct): string => {
  const type = product.productList?.[0]?.pricing?.price_type ?? 'FLAT';

  if (
    type === 'TIER' &&
    product.subTypes &&
    product.subTypes.length > 0 &&
    ['BULK FLOWER', 'SHAKE', 'STRAIN SPECIFIC SHAKE', 'PRE-PACK'].includes(
      product.subTypes[0],
    )
  ) {
    return 'TIER';
  }

  return 'FLAT';
};

const mapProduct = (product: TreezProduct): GProduct => {
  const asset = mapProductPhotoAsset(product);
  const trezPriceType = priceType(product);

  const inStock = (product.productList?.[0]?.inStock ?? 0) > 0;
  const outStock = product?.liveInventory === 0;

  return {
    id: product.productList?.[0]?.productId,
    // TODO lets make altMenuTitle or productName on a feature flag
    name:
      product?.menuTitle ||
      formatProductTitle(product.productList?.[0].productName),
    url: `${PRODUCT_URL_PATH}${product.productList?.[0].slug}`,
    slug: product.productList?.[0]?.seoProductName ?? '',
    path_slug: product.productList?.[0]?.slug ?? '',
    enabled: true, //product.productList?.[0]?.active, // We commnet this out becasue the treez sandbox has a wrong data this is only for the demo
    description: getDescription(product),
    category: startCase(product.type.toLocaleLowerCase()),
    subcategory: product.subTypes?.[0],
    category_id: product.categoryId,
    thc: getLabValue(product.thc),
    cbd: getLabValue(product.cbd),
    qty: product?.liveInventory,
    brand_name: product.brand,
    brand_id: product.brand,
    brand_subtype: startCase(
      product.productList?.[0]?.productGroup.toLocaleLowerCase(),
    ),
    // TODO add general_attr to our backend product model
    flavors: product.flavors,
    effects: product.effects,
    // @ts-ignore TODO add flavors to our backend product model
    // remove ingredients? it doesn't come from treez api
    ingredients: product.attributeMap?.INGREDIENT,
    in_stock: inStock,
    sales_price: product.productList?.[0]?.priceSell * 100,
    flower_type: startCase(product.classifications?.[0].toLocaleLowerCase()),
    cannabis_type: product.classifications?.[0] as CannabisType,
    available_for_delivery: true,
    available_for_pickup: true,
    asset: asset,
    assets: [asset],
    price_type: trezPriceType as PriceType,
    variants: mapProductVariants(product),
    discounts: filterDiscountsOutOfSchedule(
      product?.productList?.[0]?.discounts,
    ),
    labResults: product.productList?.[0]?.labResults || [],
    isOutStock: outStock,
    isLowStock: !inStock && !outStock,
    base_weight: product?.productList?.[0]?.weight,
    tier_method: product?.productList?.[0]?.pricing?.tier_method as TierMethod,
    pricing: product?.productList?.[0]?.pricing,
  };
};

// For a BOGO or BUNDLE that has not met it’s schedule condition, the API returns discount_affinity=cart.
// This is actually a bug that should be fixed, as discount_affinity should
// be “pre-cart” for a bogo. However because this bug is consistent,
// we can build logic around it for a short-term
// fix until we add API logic to filter out inactive BOGOS and BUNDLES from the response.
const filterDiscountsOutOfSchedule = (discounts?: Discount[]): Discount[] => {
  if (!discounts?.length) {
    return [];
  }

  const methodsToFilter = ['BOGO', 'BUNDLE'];

  return discounts.filter(discount => {
    const method = discount?.discount_method;

    const isSpecialCase = methodsToFilter.includes(method);

    if (!isSpecialCase) {
      return true;
    }

    return discount?.discount_affinity !== 'Cart';
  });
};

const getLabValue = (v?: string): Component => {
  let percentage = 0;
  let amount = 0;

  if (!v) {
    return { percentage, amount };
  }

  if (v.includes('%')) {
    percentage = parseFloat(v);
  }

  if (v?.toLowerCase()?.includes('mg')) {
    amount = parseFloat(v);
  }

  return { percentage, amount };
};

const getStockItem = (stockList, type, subType) => {
  if (!stockList?.length) return null;

  const formattedSub = subType?.toLowerCase()?.trim();
  const formattedType = type?.toLowerCase()?.trim();

  const getStockMatch = (searchName: string) => {
    if (!searchName) return null;

    return (
      stockList?.find(
        stock => stock?.name?.toLowerCase().trim() === searchName,
      ) || null
    );
  };

  //First search for the format 'type:subtype'
  if (formattedType && formattedSub) {
    const param = `${formattedType}:${formattedSub}`;
    const subtypeMatch = getStockMatch(param);

    if (subtypeMatch) return subtypeMatch;
  }

  // Then search by type only
  if (formattedType) {
    const typeMatch = getStockMatch(formattedType);
    if (typeMatch) return typeMatch;
  }

  return null;
};

const generateStockImage = product => {
  const productType = (data as unknown as IStoreMetadata)?.productCard;

  if (productType) {
    const stockImage = getStockItem(
      productType?.stock_image,
      product.type,
      product.subTypes?.[0],
    );

    if (stockImage) {
      return {
        image: stockImage?.default_product_image?.url,
        stock: true,
      };
    }
  }

  const imagePlaceholder = product?.image_placeholder?.url ?? PLACEHOLDER;

  return {
    image: imagePlaceholder,
    stock: true,
  };
};

const mapProductPhotoAsset = (product: TreezProduct): Asset => {
  if (product?.largeImage) {
    return { image: product.largeImage };
  }

  const stockImage = generateStockImage(product);

  if (stockImage) {
    return stockImage;
  }

  // fallback image
  return { image: PLACEHOLDER };
};

const mapProductVariants = (product: TreezProduct): Variant[] => {
  if (!product.productList) {
    return [];
  }
  const p: ProductList = product?.productList[0]; // it will only be one product, treez notes: Yes that's correct. Yeah I'm not sure why it's designed that way - part of the original version product from years ago. Wouldn't do it that way if I was redoing from scratch today
  const priceType = p?.pricing?.price_type ?? 'FLAT';

  if (priceType === 'FLAT') {
    return [mapVariant(p)];
  }

  if (priceType === 'TIER') {
    const t = p.pricing.tier_pricing_detail
      ?.filter(t => t.start_value > 0) // skip the first tier
      .map(t => mapTierVariant(p, t));

    return t;
  }

  return [];
};

const mapVariant = (product: ProductList): Variant => {
  const price = calculatePrice(String(product.displayPriceSell));
  const salePrice = getSalePrice(String(product.discountedPrice), price);

  const discount_amount = calculatePrice(
    String(product?.pricing?.discount_amount ?? '0'),
  );
  const discount_percent = parseFloat(
    product?.pricing?.discount_percent?.toFixed(2) ?? '0',
  );

  return {
    id: product.productId,
    name: product.productName,
    pos_id: product.productId,
    price: price,
    salePrice: salePrice,
    discount_amount,
    discount_percent,
    type: mapVariantType(product),
  };
};

const mapTierVariant = (
  product: ProductList,
  tierPricingDetail: TierPricingDetails,
): Variant => {
  const discount_amount = calculatePrice(
    String(product?.pricing?.discount_amount ?? '0'),
  );
  const discount_percent = parseFloat(
    product?.pricing?.discount_percent?.toFixed(2) ?? '0',
  );

  const price = calculatePrice(
    String(tierPricingDetail.price_per_value * tierPricingDetail.start_value),
  );

  const salePrice = calculateSalePrice(
    tierPricingDetail.price_per_value,
    tierPricingDetail.start_value,
    discount_percent,
  );

  return {
    id: `${product.productId}-${tierPricingDetail.start_value}`, // we are adding the weight to the id to make it unique
    name: product.productName,
    pos_id: product.productId,
    price: price,
    salePrice: salePrice,
    discount_amount,
    discount_percent,
    type: mapVariantType({ ...product, weight: tierPricingDetail.start_value }),
  };
};

const mapVariantType = (product: ProductList): string => {
  if (
    product.unitOfMeasurement === 'MILLIGRAMS' ||
    product.unitOfMeasurement === 'UNIT'
  ) {
    return 'each';
  }

  switch (product.weight) {
    case 0.5:
      return 'half_gram';
    case 1:
      return 'gram';
    case 2:
      return 'two_gram';
    case 3.5:
      return 'eighth_ounce';
    case 7:
      return 'quarter_ounce';
    case 14:
      return 'half_ounce';
    case 28:
      return 'ounce';
    default:
      return `${product.weight}_${product.unitOfMeasurement.toLowerCase()}`;
  }
};

const formatWeightLabel = (type: string): string =>
  ({
    gram: '1g',
    half_gram: '0.5g',
    two_gram: '2g',
    ounce: '28g',
    half_ounce: '14g',
    quarter_ounce: '7g',
    eighth_ounce: '3.5g',
    each: 'each',
    'Not provided': 'Not provided',
  }[type] ?? generateLabel(type));

const generateLabel = (type: string) => {
  if (!type) return '';

  return type.split('_')[1] === 'grams'
    ? `${type.split('_')[0]}g`
    : type.replace('_', ' ');
};

const getSalePrice = (discountedPrice: string | null, price: number): number =>
  discountedPrice ? calculatePrice(discountedPrice) : price;

const calculatePrice = (value: string): number => {
  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) return 0;

  // Round to 2 decimal places and convert to string
  const roundedValue = parsedValue?.toFixed(2);

  // Remove the decimal point and convert to number
  const cents = parseInt(roundedValue?.replace('.', ''), 10);

  return cents;
};

const calculateSalePrice = (
  pricePerValue: number,
  startValue: number,
  discountPercent?: number,
): number => {
  const calculatedDiscountPercentage = discountPercent
    ? discountPercent / 100
    : 0;

  if (isNaN(pricePerValue) || isNaN(startValue)) return 0;

  const roundedDiscountAmount =
    Math.round(pricePerValue * calculatedDiscountPercentage * 100) / 100;

  return calculatePrice(
    String((pricePerValue - roundedDiscountAmount) * startValue),
  );
};

const getStoresFilter = () => {
  const storeIds = process?.env?.NEXT_PUBLIC_JANE_STORES_IDS?.split(',');
  return storeIds?.map(id => `objectID:${id}`).join(' OR ');
};

const getInitialStoresFilter = () => {
  const storeIds = process?.env?.NEXT_PUBLIC_JANE_STORES_IDS?.split(',');
  return `(${storeIds?.map(id => `store_id:${id}`).join(' OR ')})`;
};

const getReviewsFeelingsCount = (reviews: any[]) =>
  Object.entries(
    reviews.reduce(
      (acc, current) => {
        const temp = acc;
        current.feelings.forEach(feeling => {
          temp[feeling] = (temp[feeling] ?? 0) + 1;
        });
        return temp;
      },
      { Blissful: 0, Creative: 0, Energetic: 0 },
    ),
  );

const getReviewsActivitiesCount = (reviews: any[]) =>
  Object.entries(
    reviews.reduce(
      (acc, current) => {
        const temp = acc;
        current.activities.forEach(activity => {
          temp[activity] = (temp[activity] ?? 0) + 1;
        });
        return temp;
      },
      {
        'Ease my mind': 0,
        'Get Active': 0,
        'Stimulate my mind': 0,
      },
    ),
  );

const feelingsIconsNameMap = {
  blissful: 'blissful',
  creative: 'creative',
  energetic: 'energetic',
  hungry: 'hungry',
  not_high: 'sentiment_satisfied',
  pain_free: 'pain_free',
  relaxed: 'relaxed',
  sleepy: 'sleepy',
};

const reviewsIconsNameMap = {
  'Ease my mind': 'ease_mind',
  'Get Active': 'vital_signs',
  'Get intimate': 'intimate',
  'Get relief': 'relief',
  'Get some sleep': 'some_sleep',
  'Hang with friends': 'hang_with_friends',
  'Stimulate my mind': 'light_bulb',
};

const getFeelingIconName = (name: string) => {
  const transformedName = name.toLowerCase().replace(/ /g, '_');
  return feelingsIconsNameMap[transformedName] ?? 'cannabis';
};

const getActivityIconName = (name: string) =>
  reviewsIconsNameMap[name] ?? 'cannabis';

const generateQueryParams = (queryParams: {
  [key: string]: string | number | boolean;
}) => {
  return Object.entries(queryParams)
    .reduce((params, [key, value]) => {
      return value ? [...params, `${key}=${value}`] : params;
    }, [])
    .join('&');
};

const getProductItemLocId = (storeId: string, weightVariant: Variant) => {
  const treezIds = new Map(
    process?.env?.NEXT_PUBLIC_TREEZ_ID_PER_STORE &&
      JSON.parse(process?.env?.NEXT_PUBLIC_TREEZ_ID_PER_STORE),
  );
  const currentTreezId = treezIds.get(storeId);

  if (!currentTreezId) {
    return `${weightVariant?.pos_id}`;
  }

  return `${currentTreezId}|${weightVariant?.pos_id}`;
};

const isBogoBetweenTheDateRange = (
  startDate: Date,
  endDate: Date | undefined,
): { currentDate: Date; isActive: boolean } => {
  const currentDate = new Date().toLocaleString('en-US', {
    hourCycle: 'h23',
    timeZone: 'America/Los_Angeles', // TODO get the info from the GC store
  });
  const formatCurrentDate = new Date(currentDate);

  const inRange = endDate ? isBefore(formatCurrentDate, endDate) : true;

  const isActive = isAfter(formatCurrentDate, startDate);

  return {
    currentDate: formatCurrentDate,
    isActive: inRange && isActive,
  };
};

const dataInDayWeek = (
  startDate: Date,
  endDate: Date | undefined,
  activeDays?: { repeatOn: { [key: string]: boolean }[] },
) => {
  const { currentDate, isActive } = isBogoBetweenTheDateRange(
    startDate,
    endDate,
  );

  if (!isActive) {
    return false;
  }

  if (!activeDays) {
    const today = format(currentDate, 'eeee');
    const start = format(startDate, 'eeee');

    return today === start;
  }

  const dayWeek = format(currentDate, 'eeee').toUpperCase().slice(0, 3);
  return activeDays.repeatOn[dayWeek];
};

const dateInRangeBy = (
  startDate: Date,
  endDate: Date | undefined,
  by: 'ANNUAL' | 'MOUNTH_DAY' | 'WEEK_DAY',
) => {
  const { currentDate, isActive } = isBogoBetweenTheDateRange(
    startDate,
    endDate,
  );

  if (!isActive) {
    return false;
  }

  if (by === 'WEEK_DAY') {
    const currentWeekDay = currentDate.getDay();
    return currentWeekDay != 0 && currentWeekDay != 6; // 0 and 6 and Sat and Sunday
  }

  // get info about Day
  const currentDay = currentDate.getUTCDate();
  const initDay = startDate.getUTCDate();

  // get info about Hours
  const currentTime = `${currentDate.getHours()}:${currentDate.getMinutes()}`;
  const startTime = `${startDate.getHours()}:${startDate.getMinutes()}`;

  if (by === 'MOUNTH_DAY') {
    return currentDay === initDay && currentTime >= startTime;
  }

  // get indo about Mounth
  const currentMounth = currentDate.getUTCMonth();
  const initMount = startDate.getUTCMonth();
  return (
    currentMounth === initMount &&
    currentDay === initDay &&
    currentTime >= startTime
  );
};

const dateInRange = (startDate: Date, endDate: Date | undefined) => {
  const { isActive } = isBogoBetweenTheDateRange(startDate, endDate);
  return isActive;
};

// Prioritizes adminWorkingHours > configWorkingHours, and ensures a consistent format
export const getActiveWorkingHours = ({
  adminWorkingHours,
  configWorkingHours,
}: {
  adminWorkingHours?: ScheduleWorkingHours[] | null;
  configWorkingHours?: DayPeriod[] | null;
}): ScheduleWorkingHours[] => {
  if (adminWorkingHours && adminWorkingHours.length > 0) {
    const hasOpenDay = adminWorkingHours.some(day => day.start && day.end);
    return hasOpenDay ? adminWorkingHours.filter(Boolean) : [];
  }

  if (configWorkingHours && configWorkingHours.length > 0) {
    const hasOpenDay = configWorkingHours.some(
      day => day.period?.from && day.period?.to,
    );
    if (!hasOpenDay) return [];

    return configWorkingHours.filter(Boolean).map(wh => ({
      day_of_the_week: wh.day.toUpperCase() as WeekDay,
      start: wh.period.from,
      end: wh.period.to,
    }));
  }

  return [];
};

const isCurrentDateInRange = (
  start: string | TimestampField,
  end: string | TimestampField,
) => {
  const currentDate = toZonedDate();

  const startDate = start ? toZonedDate(start) : null;
  const endDate = end ? toZonedDate(end) : null;

  const inRange = endDate ? currentDate <= endDate : true;
  const isActive = startDate ? currentDate >= startDate : true;

  return inRange && isActive;
};

const convertHexadecimalFormatToRGB = (hex: string) => {
  const hexParse = hex.replace(/^#/, '');

  const r = parseInt(hexParse.substring(0, 2), 16);
  const g = parseInt(hexParse.substring(2, 4), 16);
  const b = parseInt(hexParse.substring(4, 6), 16);

  return { r, g, b };
};

const getScaleColor = (color: string) => {
  const rgb = convertHexadecimalFormatToRGB(color);

  // Calculate luminance
  const luminance = 0.2126 * rgb?.r + 0.7152 * rgb?.g + 0.0722 * rgb?.b;

  const grayscaleAmount = Math.round(((255 - luminance) / 255) * 100);

  return grayscaleAmount;
};

const calculateDiscountPercent = (value: number, total: number) => {
  return 100 - Math.floor((value / total) * 100);
};

const getPreCartTreezDiscounts = (specials: TreezSpecials[]) =>
  specials?.filter(s => !s.cart && s.showEcommerce !== false);

const getSpecialLink = (
  s: TreezSpecials | PromoCarouselDocumentDataCarouselItemsItemWithID,
) =>
  `${TEMPLATE_BASE_PATH.SPECIALS}${stringToSlug(
    s?.title ??
      (s as PromoCarouselDocumentDataCarouselItemsItemWithID)?.pos_title_name,
  )}?special_id=${s?.id}`;

const getExternalStores = () => {
  // this external store config is to support a use case for Airfield store
  // so they want to be able to select the store on the store selector
  // component and redirect customers to respective cross-domain
  // store
  const s = process?.env?.NEXT_PUBLIC_EXTERNAL_STORES;
  if (!s) {
    return [];
  }

  return JSON.parse(s);
};

const getTreezStores = () => (data as unknown as IStoreMetadata)?.treezStores;

const getDefaultStore = () => {
  const treezStores = getTreezStores();

  return (
    treezStores?.find(
      ({ shortName }) =>
        shortName === process.env.NEXT_PUBLIC_TREEZ_DEFAULT_STORE_NAME,
    ) ?? treezStores?.[0]
  );
};

export {
  MapProducts,
  calculateDiscountPercent,
  calculatePrice,
  checkIfStoreIsOpen,
  compareDistances,
  convertHexadecimalFormatToRGB,
  dataInDayWeek,
  dateInRange,
  dateInRangeBy,
  findCategoryForSubtype,
  formatProductTitle,
  formatWeightLabel,
  generateQueryParams,
  getActivityIconName,
  getExternalStores,
  getFeelingIconName,
  getHoursAndMinutes,
  getInitialStoresFilter,
  getPreCartTreezDiscounts,
  getProductItemLocId,
  getReviewsActivitiesCount,
  getReviewsFeelingsCount,
  getSalePrice,
  getScaleColor,
  getSpecialLink,
  getStockItem,
  getStoresFilter,
  isCurrentDateInRange,
  mapStoreToDistance,
  mapStoreToLocation,
  transformMilitaryTime,
  getTreezStores,
  getDefaultStore,
  getLabValue,
};
